import { FC, PropsWithChildren, useState } from 'react';
import classnames from 'classnames';
import { FeedbackResponseType, WorkerCommentEntry } from '@scale/shared/types/chat';
import {
  getCommentIsOverallFeedback,
  getAuthorTagDataForFeedback,
} from '@scale/shared/chat/response/feedback';
import { Divider, Tag, Icon } from '@scale/scaleui';
import { isNil, isFinite } from 'lodash';
import FeedbackStatusDot from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/components/FeedbackStatusDot';
import { useScrollToOverallFeedback } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useScrollToOverallFeedback';
import { BinaryFeedbackGroup } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/components/BinaryFeedbackGroup';

const CHARACTER_LIMIT = 200;

type ReadOnlyFeedbackProps = PropsWithChildren<{
  id: string;
  workerComment: WorkerCommentEntry;
  isSelected: boolean;
  onAccept: (id: string) => void;
  onDismiss: (id: string) => void;
  onClick: (id: string) => void;
  enableUnifiedFeedback?: boolean;
}>;

// eslint-disable-next-line complexity
const ReadOnlyFeedback: FC<ReadOnlyFeedbackProps> = ({
  id,
  workerComment,
  isSelected,
  onAccept,
  onDismiss,
  onClick,
  enableUnifiedFeedback,
}) => {
  const { comment, feedbackResponse, author, reviewLevel, turn, title } = workerComment;
  const hasFeedbackResponse = !isNil(feedbackResponse);
  const isOverallFeedback = getCommentIsOverallFeedback(workerComment);

  /*
   * The scroll to step functionality should be disabled in certain cases / when others actions are taken
   * This means we may need to stop the propagation of the click event
   */
  const isHandleClickDisabled =
    hasFeedbackResponse || (isOverallFeedback && !enableUnifiedFeedback);

  // Only show the first 200 characters of the comment if it exceeds the limit and add a show more button
  const [showMoreText, setShowMoreText] = useState<boolean>(false);
  const commentExceedsCharacterLimit = comment.length > CHARACTER_LIMIT;
  const commentTextToShow =
    showMoreText || !commentExceedsCharacterLimit
      ? comment
      : `${comment.slice(0, CHARACTER_LIMIT).trim()}...`;

  const handleAccept = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onAccept(id);
  };
  const handleDismiss = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onDismiss(id);
  };
  const handleClick = () => {
    if (isHandleClickDisabled) {
      return;
    }
    onClick(id);
  };

  const { authorLabel, isSeniorReview } = getAuthorTagDataForFeedback(author, reviewLevel);

  return (
    <div
      className={classnames('flex flex-col bg-neutral-0 rounded-lg border', {
        'hover:cursor-pointer hover:bg-neutral-0/[.40]': !isHandleClickDisabled,
        'border-primary-300': isSelected,
        'border-neutral-300': !isSelected,
      })}
      onClick={handleClick}
    >
      <div className="flex flex-row justify-between">
        {(authorLabel || isSeniorReview) && (
          <div className="flex flex-grow-0 pt-3 px-3 justify-between items-center">
            {authorLabel && (
              <Tag size="sm" variant="neutral" icon="user">
                {authorLabel}
              </Tag>
            )}
            {isSeniorReview && (
              <Tag size="sm" variant="primary" icon="shield">
                {'Senior Review'}
              </Tag>
            )}
          </div>
        )}
        {!hasFeedbackResponse && (
          <div className="flex items-center pr-4">
            <FeedbackStatusDot />
          </div>
        )}
      </div>

      <div
        className={
          enableUnifiedFeedback
            ? 'flex flex-col p-3 items-start'
            : 'flex flex-grow-0 p-3 justify-between items-center'
        }
      >
        <div className="flex items-center justify-start gap-2">
          {isOverallFeedback && (
            <Tag size="sm" variant="neutral" icon="earth-americas">
              {!isNil(turn) && turn >= 0 ? `Turn ${turn + 1} Overall` : 'Overall'}
            </Tag>
          )}
          {!isOverallFeedback && !isNil(turn) && turn >= 0 && (
            <Tag size="sm" variant="neutral" icon="highlighter-line">
              Turn {turn + 1}
            </Tag>
          )}
          {hasFeedbackResponse && (
            <Tag size="sm" variant="success" icon="circle-check">
              Resolved
            </Tag>
          )}
        </div>
        <div className="flex items-center justify-end">
          <>
            {!hasFeedbackResponse &&
              (enableUnifiedFeedback ? (
                <>
                  <BinaryFeedbackGroup
                    message="Was this feedback helpful?"
                    helpfulness={
                      feedbackResponse === FeedbackResponseType.Accept
                        ? 'helpful'
                        : feedbackResponse === FeedbackResponseType.Dismiss
                        ? 'not_helpful'
                        : undefined
                    }
                    handleSubmitFeedback={helpfulness => {
                      if (helpfulness === 'helpful') {
                        onAccept(id);
                      } else {
                        onDismiss(id);
                      }
                    }}
                  />
                </>
              ) : (
                <>
                  <Icon
                    icon={[
                      feedbackResponse === FeedbackResponseType.Accept ? 'fas' : 'far',
                      'thumbs-up',
                    ]}
                    onClick={handleAccept}
                    tooltip="Rate this comment as helpful"
                  />
                  <Icon
                    icon={[
                      feedbackResponse === FeedbackResponseType.Dismiss ? 'fas' : 'far',
                      'thumbs-down',
                    ]}
                    onClick={handleDismiss}
                    tooltip="Rate this comment as unhelpful"
                  />
                </>
              ))}
          </>
        </div>
      </div>
      {!!title && (
        <div className="px-3 pb-3 text-neutral-700 text-sm font-semibold break-words">{title}</div>
      )}
      <div className="px-3">
        <Divider direction="horizontal" className="mb-3" />
      </div>
      <div className="px-3 pb-3 text-neutral-700 font-normal text-sm break-words">
        <div className="flex flex-col gap-1 whitespace-pre-wrap">
          {commentTextToShow}
          {commentExceedsCharacterLimit && (
            <div
              className="text-primary-400 text-xs"
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                setShowMoreText(!showMoreText);
              }}
            >
              {showMoreText ? 'Show less' : 'Show more'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type ReadOnlyInlineFeedbackDropdownProps = {
  workerComments: WorkerCommentEntry[];
  scrollToStep: (stepIndex: number) => void;
  setIsOpen: (isOpen: boolean) => void;
  handleSelectWorkerComment: (id: string) => void;
  selectedWorkerComment: string | null;
  onAccept: (id: string) => void;
  onDismiss: (id: string) => void;
  enableUnifiedFeedback?: boolean;
};

export const ReadOnlyInlineFeedbackDropdown: FC<ReadOnlyInlineFeedbackDropdownProps> = ({
  workerComments,
  scrollToStep,
  setIsOpen,
  handleSelectWorkerComment,
  selectedWorkerComment,
  onAccept,
  onDismiss,
  enableUnifiedFeedback,
}: ReadOnlyInlineFeedbackDropdownProps) => {
  const scrollToOverallFeedback = useScrollToOverallFeedback();
  if (!workerComments.length) {
    return (
      <div className="border border-neutral-300 text-center text-neutral-500 bg-neutral-0 rounded-md p-2">
        No feedback has been given yet!
      </div>
    );
  }
  const readOnlyFeedbackCard = workerComments.map(comment => (
    <ReadOnlyFeedback
      key={comment.workerCommentId}
      id={comment.workerCommentId}
      workerComment={comment}
      isSelected={comment.workerCommentId === selectedWorkerComment}
      onAccept={onAccept}
      onDismiss={onDismiss}
      onClick={(id: string) => {
        handleSelectWorkerComment(id);
        if (!isNil(comment.childId)) {
          const stepIndex = parseInt(comment.childId.split('-')[1]);
          if (isFinite(stepIndex)) {
            setIsOpen(false);
            scrollToStep(stepIndex);
          }
        } else if (enableUnifiedFeedback) {
          setIsOpen(false);
          scrollToOverallFeedback();
        }
      }}
      enableUnifiedFeedback={enableUnifiedFeedback}
    />
  ));
  return <>{readOnlyFeedbackCard}</>;
};

export default ReadOnlyInlineFeedbackDropdown;
